import { keyframes } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { isDarkTheme, isNeonTheme } from "../../utils/themeTraits";

const FALLBACK_ACCENT = "#000";

// The hero card's neon runs by turning the angle a conic gradient starts from. A custom
// property is only interpolatable once it is registered, so the `@property --hero-neon-angle`
// rule in `src/index.css` is what keeps this a lap rather than a jump at every half cycle.
const NEON_LAP_MS = 6000;
const NEON_WIDTH = "2px";

const neonRun = keyframes`
  to { --hero-neon-angle: 360deg; }
`;

// displayConfig comes straight from the tenant row, so a malformed colour there would
// otherwise throw out of alpha() and take the whole page down.
const tint = (color, opacity) => {
  try {
    return alpha(color, opacity);
  } catch {
    return alpha(FALLBACK_ACCENT, opacity);
  }
};

/**
 * `futuristic` (herevr): glass. The translucent section colour is lifted with a faint
 * accent gradient, an accent-tinted border and glow, a blurred backdrop, and a thin accent
 * bar along the top edge. Every colour is derived from the tenant's `primaryColor`.
 */
const futuristicSurface = (accent) => ({
  position: "relative",
  overflow: "hidden",
  border: `1px solid ${tint(accent, 0.18)}`,
  backgroundImage: `linear-gradient(150deg, ${tint(accent, 0.1)} 0%, ${tint(accent, 0)} 46%), ` +
    `linear-gradient(330deg, ${tint(accent, 0.07)} 0%, ${tint(accent, 0)} 42%)`,
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  boxShadow: `0 18px 40px -24px ${tint(accent, 0.55)}, 0 2px 10px ${tint("#000", 0.07)}`,
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    height: "3px",
    background: `linear-gradient(90deg, ${tint(accent, 0)}, ${accent}, ${tint(accent, 0)})`,
  },
});

/**
 * `dark` (2fadedbarbershop): the same dark section colour, framed rather than lit. A hairline in
 * the accent colour takes the place of the glow, the gradient wash and the accent bar, and the
 * card rests on a plain black shadow.
 */
const darkSurface = (accent) => ({
  border: `1px solid ${tint(accent, 0.3)}`,
  boxShadow: `0 18px 40px -24px ${tint("#000", 0.9)}`,
});

/**
 * `default` (brownsbs): paper. The section colour stays exactly as the tenant set it — no
 * gradient, no accent bar, no coloured glow — and the card is separated from the page by
 * nothing more than a hairline neutral border and a soft neutral shadow.
 */
const defaultSurface = () => ({
  border: `1px solid ${tint("#000", 0.08)}`,
  boxShadow: `0 12px 32px -20px ${tint("#000", 0.45)}, 0 1px 2px ${tint("#000", 0.04)}`,
});

/**
 * The shared look of the home page cards, per theme.
 */
export const sectionSx = (displayConfig) => {
  const accent = displayConfig?.primaryColor || FALLBACK_ACCENT;

  return {
    borderRadius: 3,
    color: displayConfig?.color,
    backgroundColor: displayConfig?.sectionBackground,
    ...(isNeonTheme(displayConfig)
      ? futuristicSurface(accent)
      : isDarkTheme(displayConfig) ? darkSurface(accent) : defaultSurface()),
  };
};

/**
 * The hero welcome card is the one section that sits on top of the hero photo, so the
 * translucent section colour alone leaves the photo showing through behind the title. A
 * black scrim is laid under the accent gradients — beneath them, so the glass keeps its
 * colour — to darken the panel without touching the tenant's `sectionBackground`.
 *
 * It also trades the static top accent bar for the same neon running the whole way round:
 * a comet of accent light chases the border, and the `&::before` here replaces — not
 * extends — the one `futuristicSurface` sets up.
 *
 * Dark themes only: darkening a light tenant's white card would leave grey
 * paper under its black text, and a neon lap does not belong on its paper. Paper squares the
 * card off instead (owner, 2026-09-12): it is a panel of the hero row, sitting flush against
 * the photo, not a card floating on the page — rounded corners there only fought the edge it
 * meets. The rounding stays on every other section, and on the glass one here.
 */
export const heroSectionSx = (displayConfig) => {
  const base = sectionSx(displayConfig);

  if (!isDarkTheme(displayConfig)) return { ...base, borderRadius: 0 };

  const accent = displayConfig?.primaryColor || FALLBACK_ACCENT;
  const scrim = `linear-gradient(${tint("#000", 0.6)}, ${tint("#000", 0.6)})`;
  const scrimmed = {
    ...base,
    backgroundImage: base.backgroundImage ? `${base.backgroundImage}, ${scrim}` : scrim,
  };

  // The running light is neon; the dark theme without it keeps its hairline frame.
  if (!isNeonTheme(displayConfig)) return scrimmed;

  return {
    ...scrimmed,
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: "inherit",
      padding: NEON_WIDTH,
      // A comet: transparent for most of the turn, brightening to the accent and then to
      // white at its head. The tail ends on a transparent *accent*, never on `transparent`
      // itself, which some engines interpolate through black and would smear grey.
      background: `conic-gradient(from var(--hero-neon-angle, 0deg), ` +
        `${tint(accent, 0)} 0deg, ${tint(accent, 0.55)} 260deg, ${accent} 330deg, ` +
        `#fff 348deg, ${tint(accent, 0)} 356deg)`,
      // Paint the padding ring only: the two masks cover the same box, so compositing them
      // so they cancel leaves exactly the strip the padding carves out.
      WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
      WebkitMaskComposite: "xor",
      mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
      maskComposite: "exclude",
      animation: `${neonRun} ${NEON_LAP_MS}ms linear infinite`,
      pointerEvents: "none",
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    },
  };
};

/**
 * The same frame for content that cannot carry a background of its own — the map iframe.
 */
export const frameSx = (displayConfig) => {
  const accent = displayConfig?.primaryColor || FALLBACK_ACCENT;

  return {
    borderRadius: 3,
    ...(isNeonTheme(displayConfig)
      ? {
          border: `1px solid ${tint(accent, 0.18)}`,
          boxShadow: `0 18px 40px -24px ${tint(accent, 0.55)}`,
        }
      : isDarkTheme(displayConfig) ? darkSurface(accent) : {
          border: `1px solid ${tint("#000", 0.08)}`,
          boxShadow: `0 12px 32px -20px ${tint("#000", 0.45)}`,
        }),
  };
};
